import {
  ArrowDownAZ,
  ArrowUpAZ,
  Check,
  ChevronLeft,
  CloudOff,
  Database,
  HardDrive,
  type LucideIcon,
  Pause,
  PauseCircle,
  Play,
  RotateCw,
  Trash2,
  X,
} from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { toast } from 'sonner'
import { useShallow } from 'zustand/react/shallow'

import { PageTabs } from '@/components/shared/page-tabs'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useEpisodeDownload } from '@/hooks/useEpisodeDownload'
import { useOnlineStatus } from '@/hooks/useOnlineStatus'
import { confirmDialog } from '@/lib/confirm-dialog'
import { HEADER_HEIGHT } from '@/lib/constants'
import {
  deleteAudio,
  deleteOfflineEpisode,
  deleteRecording,
  discardPendingDownload,
  getAllAudios,
  getAllOfflineEpisodes,
  getAllPendingDownloads,
  getAllRecordings,
  type OfflineEpisode,
  type PendingDownload,
  type StoredAudio,
  type StoredRecording,
} from '@/lib/db'
import { formatBytes, formatDateTimeNumeric } from '@/lib/format-utils'
import { type SortField, sortItems, type SortOrder } from '@/lib/sort-utils'
import { useAppStore } from '@/store/useAppStore'
import { useDownloadStore } from '@/store/useDownloadStore'

/** 進行中とみなすダウンロードステータス */
const ACTIVE_DOWNLOAD_STATUSES = new Set(['queued', 'downloading', 'saving'])

type TableType = 'audios' | 'recordings' | 'offline' | 'pending'

// 共通のチェックボックスコンポーネント
const Checkbox = ({ checked, className = '' }: { checked: boolean; className?: string }) => (
  <div
    className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${
      checked ? 'bg-primary border-primary' : 'border-input hover:border-primary/50'
    } ${className}`}
  >
    {checked && <Check className="w-4 h-4 text-primary-foreground" strokeWidth={3} />}
  </div>
)

// 全選択行コンポーネント
const SelectAllRow = ({ checked, onClick }: { checked: boolean; onClick: () => void }) => (
  <div className="ps-3 sm:p-4 shadow-sm mb-2">
    <div onClick={onClick} className="flex items-center cursor-pointer group">
      <Checkbox checked={checked} />
      <span className="font-semibold text-sm ps-2">全選択</span>
    </div>
  </div>
)

// 共通のアイテム情報表示コンポーネント
const ItemInfo = ({ label, value, className = '' }: { label: string; value: string | number; className?: string }) => (
  <p className={`truncate ${className}`}>
    {label}: <span className="font-mono">{value}</span>
  </p>
)

// 共通のストレージアイテムコンポーネント
type StorageItemProps = {
  isSelected: boolean
  selectionMode: boolean
  onToggle: () => void
  /** 未指定の場合は削除ボタンを表示しない */
  onDelete?: () => void
  onPlay?: () => void
  /** onPlay ボタンのアイコン（既定は再生） */
  actionIcon?: LucideIcon
  /** onPlay ボタンの title 属性 */
  actionLabel?: string
  title: string
  thumbnailUrl?: string
  children: React.ReactNode
}

const StorageItem = ({
  isSelected,
  selectionMode,
  onToggle,
  onDelete,
  onPlay,
  actionIcon: ActionIcon = Play,
  actionLabel = '再生',
  title,
  thumbnailUrl,
  children,
}: StorageItemProps) => (
  <div
    onClick={() => selectionMode && onToggle()}
    className={`bg-card rounded-lg p-3 sm:p-4 shadow-sm border hover:shadow-md transition-all ${
      selectionMode ? 'cursor-pointer' : ''
    } ${isSelected ? 'ring-2 ring-primary bg-primary/5' : ''}`}
  >
    <div className="flex items-center justify-between gap-2 sm:gap-4">
      {selectionMode && (
        <div className="shrink-0">
          <Checkbox checked={isSelected} />
        </div>
      )}
      {thumbnailUrl && (
        <img src={thumbnailUrl} alt="" className="w-12 h-12 sm:w-16 sm:h-16 rounded object-cover shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold truncate mb-1 text-sm sm:text-base">{title}</h3>
        <div className="space-y-0.5 sm:space-y-1 text-[10px] sm:text-xs text-muted-foreground">{children}</div>
      </div>
      {!selectionMode && (
        <div className="flex items-center gap-1 shrink-0">
          {onPlay && (
            <Button
              onClick={onPlay}
              variant="outline"
              size="sm"
              className="h-8 w-8 sm:h-9 sm:w-auto sm:px-3"
              title={actionLabel}
            >
              <ActionIcon className="h-3 w-3 sm:h-4 sm:w-4" />
            </Button>
          )}
          {onDelete && (
            <Button onClick={onDelete} variant="destructive" size="sm" className="h-8 w-8 sm:h-9 sm:w-auto sm:px-3">
              <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
            </Button>
          )}
        </div>
      )}
    </div>
  </div>
)

// 空状態コンポーネント
const EmptyState = ({ message }: { message: string }) => (
  <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
    <Database className="h-12 w-12 sm:h-16 sm:w-16 mb-4 opacity-20" />
    <p className="text-sm sm:text-base">{message}</p>
  </div>
)

export const StorageManagement = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { isOnline } = useOnlineStatus()
  const { downloadEpisode, abort } = useEpisodeDownload()
  const downloads = useDownloadStore((s) => s.downloads)
  const { startLoading, stopLoading } = useAppStore(useShallow((state) => state))

  const activeTab = (searchParams.get('tab') as TableType) ?? 'audios'
  const [audios, setAudios] = useState<StoredAudio[]>([])
  const [recordings, setRecordings] = useState<StoredRecording[]>([])
  const [offlineEpisodes, setOfflineEpisodes] = useState<OfflineEpisode[]>([])
  const [pendingDownloads, setPendingDownloads] = useState<PendingDownload[]>([])
  const [sortField, setSortField] = useState<SortField>('date')
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')
  const [selectionMode, setSelectionMode] = useState(false)
  const [selectedAudioIds, setSelectedAudioIds] = useState<Set<string>>(new Set())
  const [selectedRecordingIds, setSelectedRecordingIds] = useState<Set<string>>(new Set())
  const [selectedOfflineIds, setSelectedOfflineIds] = useState<Set<number>>(new Set())
  const [selectedPendingIds, setSelectedPendingIds] = useState<Set<number>>(new Set())

  const loadData = useCallback(async () => {
    startLoading()
    try {
      const [allAudios, allRecordings, allOffline, allPending] = await Promise.all([
        getAllAudios(),
        getAllRecordings(),
        getAllOfflineEpisodes(),
        getAllPendingDownloads(),
      ])
      setAudios(allAudios)
      setRecordings(allRecordings)
      setOfflineEpisodes(allOffline)
      setPendingDownloads(allPending)
    } catch (error) {
      console.error('Failed to load storage data:', error)
      toast.error('データの読み込みに失敗しました')
    } finally {
      stopLoading()
    }
  }, [startLoading, stopLoading])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleDelete = useCallback(
    async (audio: StoredAudio) => {
      if (!(await confirmDialog({ title: `「${audio.title}」を削除しますか？`, destructive: true }))) return

      try {
        await deleteAudio(audio.domain, audio.productId, audio.episodeId)
        toast.success('削除しました')
        loadData()
      } catch (error) {
        console.error('Failed to delete:', error)
        toast.error('削除に失敗しました')
      }
    },
    [loadData],
  )

  const handleDeleteRecording = useCallback(
    async (recording: StoredRecording) => {
      if (!(await confirmDialog({ title: 'この録音を削除しますか？', destructive: true }))) return

      try {
        await deleteRecording(recording.id)
        toast.success('削除しました')
        loadData()
      } catch (error) {
        console.error('Failed to delete:', error)
        toast.error('削除に失敗しました')
      }
    },
    [loadData],
  )

  const handleDeleteOffline = useCallback(
    async (episode: OfflineEpisode) => {
      if (
        !(await confirmDialog({ title: `「${episode.title}」のオフラインデータを削除しますか？`, destructive: true }))
      )
        return

      try {
        await deleteOfflineEpisode(episode.episodeId)
        toast.success('削除しました')
        loadData()
      } catch (error) {
        console.error('Failed to delete:', error)
        toast.error('削除に失敗しました')
      }
    },
    [loadData],
  )

  const handleDiscardPending = useCallback(
    async (pending: PendingDownload) => {
      if (!(await confirmDialog({ title: `「${pending.title}」の中断データを削除しますか？`, destructive: true })))
        return

      try {
        await discardPendingDownload(pending.episodeId)
        toast.success('削除しました')
        loadData()
      } catch (error) {
        console.error('Failed to discard pending download:', error)
        toast.error('削除に失敗しました')
      }
    },
    [loadData],
  )

  const handleResumePending = useCallback(
    async (pending: PendingDownload) => {
      await downloadEpisode({
        domain: pending.domain,
        episodeId: pending.episodeId,
        mimeType: pending.mimeType,
        productId: pending.productId,
        title: pending.title,
      })
      loadData()
    },
    [downloadEpisode, loadData],
  )

  const getTotalSize = (items: (StoredAudio | StoredRecording | OfflineEpisode)[]) => {
    return items.reduce((sum, item) => sum + item.fileSize, 0)
  }

  const pendingTotalSize = useMemo(
    () => pendingDownloads.reduce((sum, item) => sum + item.receivedBytes, 0),
    [pendingDownloads],
  )

  // ソート済みのデータを返す
  const sortedAudios = useMemo(
    () =>
      sortItems(audios, sortField, sortOrder, {
        dateFn: (a) => a.savedAt,
        titleFn: (a) => a.title,
        sizeFn: (a) => a.fileSize,
      }),
    [audios, sortField, sortOrder],
  )

  const sortedRecordings = useMemo(
    () =>
      sortItems(recordings, sortField, sortOrder, {
        dateFn: (a) => a.savedAt,
        titleFn: (a) => `${a.domain}-${a.productId}`,
        sizeFn: (a) => a.fileSize,
      }),
    [recordings, sortField, sortOrder],
  )

  const sortedOffline = useMemo(
    () =>
      sortItems(offlineEpisodes, sortField, sortOrder, {
        dateFn: (a) => a.downloadedAt,
        titleFn: (a) => a.title,
        sizeFn: (a) => a.fileSize,
      }),
    [offlineEpisodes, sortField, sortOrder],
  )

  const sortedPending = useMemo(
    () =>
      sortItems(pendingDownloads, sortField, sortOrder, {
        dateFn: (a) => a.updatedAt,
        titleFn: (a) => a.title,
        sizeFn: (a) => a.receivedBytes,
      }),
    [pendingDownloads, sortField, sortOrder],
  )

  // サムネイルBlobからObjectURLを生成（メモリリーク防止のためクリーンアップ）
  const thumbnailUrls = useMemo(() => {
    const map = new Map<number, string>()
    for (const ep of offlineEpisodes) {
      if (ep.thumbnailBlob) {
        map.set(ep.episodeId, URL.createObjectURL(ep.thumbnailBlob))
      }
    }
    return map
  }, [offlineEpisodes])

  useEffect(() => {
    return () => {
      for (const url of thumbnailUrls.values()) {
        URL.revokeObjectURL(url)
      }
    }
  }, [thumbnailUrls])

  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
  }

  const toggleSelectionMode = () => {
    setSelectionMode((prev) => !prev)
    setSelectedAudioIds(new Set())
    setSelectedRecordingIds(new Set())
    setSelectedOfflineIds(new Set())
    setSelectedPendingIds(new Set())
  }

  const toggleSelectAll = () => {
    if (activeTab === 'audios') {
      if (selectedAudioIds.size === sortedAudios.length) {
        setSelectedAudioIds(new Set())
      } else {
        setSelectedAudioIds(new Set(sortedAudios.map((a) => a.id)))
      }
    } else if (activeTab === 'recordings') {
      if (selectedRecordingIds.size === sortedRecordings.length) {
        setSelectedRecordingIds(new Set())
      } else {
        setSelectedRecordingIds(new Set(sortedRecordings.map((r) => r.id)))
      }
    } else if (activeTab === 'offline') {
      if (selectedOfflineIds.size === sortedOffline.length) {
        setSelectedOfflineIds(new Set())
      } else {
        setSelectedOfflineIds(new Set(sortedOffline.map((o) => o.episodeId)))
      }
    } else if (activeTab === 'pending') {
      if (selectedPendingIds.size === sortedPending.length) {
        setSelectedPendingIds(new Set())
      } else {
        setSelectedPendingIds(new Set(sortedPending.map((p) => p.episodeId)))
      }
    }
  }

  const exitSelectionMode = () => {
    setSelectionMode(false)
    setSelectedAudioIds(new Set())
    setSelectedRecordingIds(new Set())
    setSelectedOfflineIds(new Set())
    setSelectedPendingIds(new Set())
  }

  const toggleAudioSelection = (audioId: string) => {
    setSelectedAudioIds((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(audioId)) {
        newSet.delete(audioId)
      } else {
        newSet.add(audioId)
      }
      return newSet
    })
  }

  const toggleRecordingSelection = (recordingId: string) => {
    setSelectedRecordingIds((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(recordingId)) {
        newSet.delete(recordingId)
      } else {
        newSet.add(recordingId)
      }
      return newSet
    })
  }

  const toggleOfflineSelection = (episodeId: number) => {
    setSelectedOfflineIds((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(episodeId)) {
        newSet.delete(episodeId)
      } else {
        newSet.add(episodeId)
      }
      return newSet
    })
  }

  const togglePendingSelection = (episodeId: number) => {
    setSelectedPendingIds((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(episodeId)) {
        newSet.delete(episodeId)
      } else {
        newSet.add(episodeId)
      }
      return newSet
    })
  }

  const getSelectedCount = () => {
    if (activeTab === 'audios') return selectedAudioIds.size
    if (activeTab === 'recordings') return selectedRecordingIds.size
    if (activeTab === 'pending') return selectedPendingIds.size
    return selectedOfflineIds.size
  }

  const handleBulkDelete = async () => {
    const itemCount = getSelectedCount()
    if (itemCount === 0) {
      toast.error('削除するアイテムを選択してください')
      return
    }

    if (!(await confirmDialog({ title: `選択した${itemCount}件を削除しますか？`, destructive: true }))) return

    try {
      if (activeTab === 'audios') {
        await Promise.all(
          Array.from(selectedAudioIds).map((id) => {
            const audio = audios.find((a) => a.id === id)
            if (audio) {
              return deleteAudio(audio.domain, audio.productId, audio.episodeId)
            }
            return Promise.resolve()
          }),
        )
      } else if (activeTab === 'recordings') {
        await Promise.all(Array.from(selectedRecordingIds).map((id) => deleteRecording(id)))
      } else if (activeTab === 'offline') {
        await Promise.all(Array.from(selectedOfflineIds).map((id) => deleteOfflineEpisode(id)))
      } else if (activeTab === 'pending') {
        await Promise.all(Array.from(selectedPendingIds).map((id) => discardPendingDownload(id)))
      }
      toast.success(`${itemCount}件を削除しました`)
      exitSelectionMode()
      loadData()
    } catch (error) {
      console.error('Failed to delete:', error)
      toast.error('削除に失敗しました')
    }
  }

  return (
    <div className="w-full h-screen fixed overflow-auto">
      <div
        className="absolute w-full"
        style={{
          top: HEADER_HEIGHT,
          height: `calc(100vh - ${HEADER_HEIGHT})`,
        }}
      >
        <PageTabs
          defaultTab="audios"
          contentClassName="flex-1 overflow-y-auto p-2 sm:p-4"
          tabs={[
            {
              value: 'audios',
              label: <>音声データ ({audios.length})</>,
              content:
                audios.length === 0 ? (
                  <EmptyState message="保存されたデータがありません" />
                ) : (
                  <div className="space-y-2 sm:space-y-3">
                    {selectionMode && (
                      <SelectAllRow checked={selectedAudioIds.size === sortedAudios.length} onClick={toggleSelectAll} />
                    )}
                    {sortedAudios.map((audio) => {
                      const isSelected = selectedAudioIds.has(audio.id)
                      return (
                        <StorageItem
                          key={audio.id}
                          isSelected={isSelected}
                          selectionMode={selectionMode}
                          onToggle={() => toggleAudioSelection(audio.id)}
                          onDelete={() => handleDelete(audio)}
                          title={audio.title}
                        >
                          <ItemInfo label="Product ID" value={audio.productId} />
                          <ItemInfo label="Episode ID" value={audio.episodeId} />
                          <p>サイズ: {formatBytes(audio.fileSize)}</p>
                          <p className="hidden sm:block">保存日時: {formatDateTimeNumeric(audio.savedAt)}</p>
                          <p className="sm:hidden">
                            {new Date(audio.savedAt).toLocaleDateString('ja-JP', {
                              month: '2-digit',
                              day: '2-digit',
                            })}
                          </p>
                          {audio.transcription && (
                            <p className="text-green-600 dark:text-green-400 truncate">
                              ✓ 文字起こし ({audio.transcription.segments.length})
                            </p>
                          )}
                        </StorageItem>
                      )
                    })}
                  </div>
                ),
            },
            {
              value: 'recordings',
              label: <>録音データ ({recordings.length})</>,
              content:
                recordings.length === 0 ? (
                  <EmptyState message="録音データがありません" />
                ) : (
                  <div className="space-y-2 sm:space-y-3">
                    {selectionMode && (
                      <SelectAllRow
                        checked={selectedRecordingIds.size === sortedRecordings.length}
                        onClick={toggleSelectAll}
                      />
                    )}
                    {sortedRecordings.map((recording) => {
                      const isSelected = selectedRecordingIds.has(recording.id)
                      return (
                        <StorageItem
                          key={recording.id}
                          isSelected={isSelected}
                          selectionMode={selectionMode}
                          onToggle={() => toggleRecordingSelection(recording.id)}
                          onDelete={() => handleDeleteRecording(recording)}
                          title={`${recording.domain} - ${recording.productId}`}
                        >
                          <ItemInfo label="Episode ID" value={recording.episodeId} />
                          <p>
                            録音時間: {Math.floor(recording.duration / 60)}:
                            {(recording.duration % 60).toString().padStart(2, '0')}
                          </p>
                          <p>サイズ: {formatBytes(recording.fileSize)}</p>
                          <p className="hidden sm:block">保存日時: {formatDateTimeNumeric(recording.savedAt)}</p>
                          <p className="sm:hidden">
                            {new Date(recording.savedAt).toLocaleDateString('ja-JP', {
                              month: '2-digit',
                              day: '2-digit',
                            })}
                          </p>
                          {recording.gradingResult?.raw_response?.NBest?.[0] && (
                            <p className="text-green-600 dark:text-green-400 truncate">
                              ✓ 採点済み (
                              {recording.gradingResult.raw_response.NBest[0].PronunciationAssessment.PronScore.toFixed(
                                1,
                              )}
                              点)
                            </p>
                          )}
                        </StorageItem>
                      )
                    })}
                  </div>
                ),
            },
            {
              value: 'offline',
              label: (
                <>
                  <CloudOff className="h-3 w-3 mr-1" />
                  オフライン ({offlineEpisodes.length})
                </>
              ),
              content:
                offlineEpisodes.length === 0 ? (
                  <EmptyState message="オフラインデータがありません" />
                ) : (
                  <div className="space-y-2 sm:space-y-3">
                    {selectionMode && (
                      <SelectAllRow
                        checked={selectedOfflineIds.size === sortedOffline.length}
                        onClick={toggleSelectAll}
                      />
                    )}
                    {sortedOffline.map((ep) => {
                      const isSelected = selectedOfflineIds.has(ep.episodeId)
                      return (
                        <StorageItem
                          key={ep.episodeId}
                          isSelected={isSelected}
                          selectionMode={selectionMode}
                          onToggle={() => toggleOfflineSelection(ep.episodeId)}
                          onDelete={() => handleDeleteOffline(ep)}
                          onPlay={() => navigate(`/p/${ep.episodeId}`)}
                          title={ep.title}
                          thumbnailUrl={thumbnailUrls.get(ep.episodeId)}
                        >
                          <ItemInfo label="ドメイン" value={ep.domain} />
                          <ItemInfo label="Episode ID" value={ep.episodeId} />
                          <p>サイズ: {formatBytes(ep.fileSize)}</p>
                          <p className="hidden sm:block">保存日時: {formatDateTimeNumeric(ep.downloadedAt)}</p>
                          <p className="sm:hidden">
                            {new Date(ep.downloadedAt).toLocaleDateString('ja-JP', {
                              month: '2-digit',
                              day: '2-digit',
                            })}
                          </p>
                          <p className="text-xs text-muted-foreground">{ep.mimeType}</p>
                        </StorageItem>
                      )
                    })}
                  </div>
                ),
            },
            {
              value: 'pending',
              label: (
                <>
                  <PauseCircle className="h-3 w-3 mr-1" />
                  中断中 ({pendingDownloads.length})
                </>
              ),
              content:
                pendingDownloads.length === 0 ? (
                  <EmptyState message="中断中のダウンロードはありません" />
                ) : (
                  <div className="space-y-2 sm:space-y-3">
                    {selectionMode && (
                      <SelectAllRow
                        checked={selectedPendingIds.size === sortedPending.length}
                        onClick={toggleSelectAll}
                      />
                    )}
                    {sortedPending.map((pending) => {
                      const isSelected = selectedPendingIds.has(pending.episodeId)
                      const activeInfo = downloads.get(pending.episodeId)
                      const isActive = activeInfo ? ACTIVE_DOWNLOAD_STATUSES.has(activeInfo.status) : false
                      // 実行中はストアのライブ進捗を優先し、それ以外は IndexedDB の保存値を使う
                      const rawPercentage = isActive
                        ? (activeInfo?.progress ?? 0)
                        : pending.totalBytes > 0
                          ? (pending.receivedBytes / pending.totalBytes) * 100
                          : 0
                      const percentage = Math.min(100, Math.max(0, Math.floor(rawPercentage)))
                      return (
                        <StorageItem
                          key={pending.episodeId}
                          isSelected={isSelected}
                          selectionMode={selectionMode}
                          onToggle={() => togglePendingSelection(pending.episodeId)}
                          onDelete={isActive ? undefined : () => handleDiscardPending(pending)}
                          onPlay={
                            isActive
                              ? () => abort(pending.episodeId)
                              : isOnline
                                ? () => handleResumePending(pending)
                                : undefined
                          }
                          actionIcon={isActive ? Pause : RotateCw}
                          actionLabel={isActive ? '一時停止' : '再開'}
                          title={pending.title}
                        >
                          <ItemInfo label="ドメイン" value={pending.domain} />
                          <ItemInfo label="Episode ID" value={pending.episodeId} />
                          <p>
                            {isActive ? 'ダウンロード中' : '進捗'}: {formatBytes(pending.receivedBytes)}
                            {pending.totalBytes > 0 && ` / ${formatBytes(pending.totalBytes)}`} ({percentage}%)
                          </p>
                          <div className="h-1 w-full rounded-full bg-muted overflow-hidden">
                            <div className="h-full bg-primary transition-all" style={{ width: `${percentage}%` }} />
                          </div>
                          <p className="hidden sm:block">最終更新: {formatDateTimeNumeric(pending.updatedAt)}</p>
                          <p className="sm:hidden">
                            {new Date(pending.updatedAt).toLocaleDateString('ja-JP', {
                              month: '2-digit',
                              day: '2-digit',
                            })}
                          </p>
                        </StorageItem>
                      )
                    })}
                  </div>
                ),
            },
          ]}
          afterTabs={
            <div className="p-3 sm:p-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2 text-xs sm:text-sm">
                  <HardDrive className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground shrink-0" />
                  <span className="font-medium">合計容量:</span>
                  <span className="font-bold text-primary">
                    {activeTab === 'pending'
                      ? formatBytes(pendingTotalSize)
                      : formatBytes(
                          getTotalSize(
                            activeTab === 'audios' ? audios : activeTab === 'recordings' ? recordings : offlineEpisodes,
                          ),
                        )}
                  </span>
                </div>

                {/* ソートボタン */}
                <div className="flex items-center gap-1">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="h-7 sm:h-8 text-xs">
                        {sortField === 'date' && '日時'}
                        {sortField === 'title' && 'タイトル'}
                        {sortField === 'size' && 'サイズ'}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuRadioGroup
                        value={sortField}
                        onValueChange={(value) => setSortField(value as SortField)}
                      >
                        <DropdownMenuRadioItem value="date">日時</DropdownMenuRadioItem>
                        <DropdownMenuRadioItem value="title">タイトル</DropdownMenuRadioItem>
                        <DropdownMenuRadioItem value="size">サイズ</DropdownMenuRadioItem>
                      </DropdownMenuRadioGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <Button variant="outline" size="sm" className="h-7 w-7 sm:h-8 sm:w-8 p-0" onClick={toggleSortOrder}>
                    {sortOrder === 'asc' ? (
                      <ArrowUpAZ className="h-3 w-3 sm:h-4 sm:w-4" />
                    ) : (
                      <ArrowDownAZ className="h-3 w-3 sm:h-4 sm:w-4" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          }
        />
      </div>
      <div className="fixed top-0 left-0 right-0">
        <div
          className="absolute top-0 right-0 left-0 bg-background z-10 border-b border-gray-600"
          style={{
            height: HEADER_HEIGHT,
          }}
        >
          <div className="flex justify-between items-center h-full px-2">
            <div className="flex gap-2 items-center flex-1 min-w-0">
              {selectionMode ? (
                <X size={40} onClick={exitSelectionMode} className="shrink-0 cursor-pointer" />
              ) : (
                <ChevronLeft
                  size={40}
                  onClick={() => (isOnline ? navigate(-1) : navigate('/'))}
                  className="shrink-0 cursor-pointer"
                />
              )}
              <div className="text-white whitespace-nowrap overflow-hidden text-ellipsis flex-1 min-w-0">
                {selectionMode ? `${getSelectedCount()}件選択中` : 'ストレージ管理'}
              </div>
            </div>
            <div className="shrink-0 ml-2 p-0!">
              {selectionMode ? (
                <Button
                  onClick={handleBulkDelete}
                  variant="destructive"
                  size="sm"
                  className="shrink-0 h-8 w-8 sm:h-9 sm:w-auto sm:px-3"
                  disabled={getSelectedCount() === 0}
                >
                  <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                </Button>
              ) : (
                <Button onClick={toggleSelectionMode} variant="outline" size="sm">
                  選択
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
